import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import type { DataGridTable } from "../columns/types";
import { getMatchingQuery, splitMatches } from "./highlightMatches";
import styles from "./cellContent.module.scss";

interface HighlightedTextProps<TData extends RowData> {
    text: string;
    table: DataGridTable<TData>;
}

/**
 * `text` with matches of the table's search (global filter) marked. Subscribes
 * to the global filter itself and selects only whether this text matches, so
 * cells without a match don't re-render while the user types.
 */
export const HighlightedText = <TData extends RowData>({
    text,
    table,
}: Readonly<HighlightedTextProps<TData>>) => {
    const query = useSelector(table.atoms.globalFilter, (filter) =>
        getMatchingQuery(text, filter),
    );
    if (query === "") {
        return text;
    }
    return splitMatches(text, query).map((part, index) =>
        part.isMatch ? (
            <mark key={index} className={styles.highlight}>
                {part.text}
            </mark>
        ) : (
            part.text
        ),
    );
};
